import { useQuery } from '@tanstack/react-query';
import React, {
    createContext,
    useContext,
    useState,
    ReactNode,
    useEffect,
} from 'react';
import { qk } from '../../lib';
import { getMe } from '../../features/auth';
import { useFetchQuickFilterDetails } from '../../mutations';

/* ================= TYPES ================= */
export type Filters = Record<FilterKey, string[]>;

export type FilterKey = 'programme' | 'region' | 'site' | 'workstream';
export type OptionType = 'programme' | 'region' | 'network' | 'site' | 'workstream' | 'stage-gates' | 'county' | 'iln' | 'worktype' | 'worktype-group' | 'phases' | 'contractor' | 'templates' | 'coam-area';

export type FilterContextType = {
    options: Record<OptionType, any[]>;
    loading: boolean;
    error: Record<OptionType, string | null>;
    quickFilters: Filters;
    tempFilters: Filters;
    handleUpdateTempFilters: (key: FilterKey, value: string[]) => void;
    handleApplyFilters: () => void;
    handleResetFilters: () => void;
};

/* ================= CONTEXT ================= */

const FilterContext = createContext<FilterContextType | undefined>(undefined);

/* ================= PROVIDER ================= */

export const FilterContextProvider: React.FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { data: session } = useQuery({
        queryKey: qk.me(),
        queryFn: getMe,
    });

    const masterDataService = useFetchQuickFilterDetails();

    const [loading, setLoading] = useState(false);

    const [error, setError] = useState<Record<OptionType, string | null>>({
        'programme': null,
        'region': null,
        'site': null,
        'stage-gates': null,
        'county': null,
        'iln': null,
        'workstream': null,
        'worktype': null,
        'worktype-group': null,
        'phases': null,
        'contractor': null,
        'templates': null,
        'coam-area': null,
        'network': null,
    });

    const optionsList: OptionType[] = [
        'programme',
        'region',
        'site',
        'stage-gates',
        'county',
        'iln',
        'workstream',
        'worktype',
        'worktype-group',
        'phases',
        'contractor',
        'templates',
        'coam-area',
        'network',
    ];

    const [options, setOptions] = useState<Record<OptionType, any[]>>({
        'programme': [],
        'region': [],
        'site': [],
        'stage-gates': [],
        'county': [],
        'iln': [],
        'workstream': [],
        'worktype': [],
        'worktype-group': [],
        'phases': [],
        'contractor': [],
        'templates': [],
        'coam-area': [],
        'network': [],
    });

    const [quickFilters, setQuickFilters] = useState<Filters>({
        programme: [],
        region: [],
        site: [],
        workstream: [],
    });

    const [tempFilters, setTempFilters] = useState<Filters>({
        programme: [],
        region: [],
        site: [],
        workstream: [],
    });

    /* ================= HANDLERS ================= */

    const handleUpdateTempFilters = (key: FilterKey, value: string[]) => {
        setTempFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleApplyFilters = () => {
        setQuickFilters({ ...tempFilters });
    };

    const handleResetFilters = () => {
        const empty: Filters = {
            programme: [],
            region: [],
            site: [],
            workstream: [],
        };
        setTempFilters(empty);
        setQuickFilters(empty);
    };

    /* ================= LOAD OPTIONS ================= */

    const loadOptions = async () => {
        try {
            setLoading(true);

            setError(
                optionsList.reduce(
                    (acc, key) => ({ ...acc, [key]: null }),
                    {} as Record<OptionType, string | null>,
                ),
            );

            const results = await Promise.all(
                optionsList.map(async (key) => {
                    try {
                        const res = await masterDataService.mutateAsync(key);
                        return [key, res.data] as const;
                    } catch {
                        return [key, null] as const;
                    }
                }),
            );

            const newOptions = results.reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: value ?? [],
                }),
                {} as Record<OptionType, any[]>,
            );

            const newErrors = results.reduce(
                (acc, [key, value]) => ({
                    ...acc,
                    [key]: value ? null : 'Failed to load',
                }),
                {} as Record<OptionType, string | null>,
            );

            setOptions(newOptions);
            setError(newErrors);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (session?.authenticated) {
            loadOptions();
        }
    }, [session?.authenticated]);

    /* ================= PROVIDER ================= */

    return (
        <FilterContext.Provider
            value={{
                options,
                loading,
                error,
                quickFilters,
                tempFilters,
                handleUpdateTempFilters,
                handleApplyFilters,
                handleResetFilters,
            }}
        >
            {children}
        </FilterContext.Provider>
    );
};

/* ================= HOOK ================= */

export const useFilterContext = () => {
    const context = useContext(FilterContext);
    if (!context) {
        throw new Error(
            'useFilterContext must be used within a FilterContextProvider',
        );
    }
    return context;
};